import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Button,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel,
    Select,
    Stack,
    Text,
    Textarea,
    useColorModeValue,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ListPageCardHeader from "../../../components/Card/ListPageCardHeader";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import {
    POOL_DECISION,
    PROCESS_STATUS,
    processesService
} from "../../../services/processesService";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { ROUTES } from "../../../constants/routes-path";
import Loading from "../../../components/Loading/Loading";
import AffirmativeDialog from "../../../components/Dialog/affirmativeDialog";

export function FillFinalDecision() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const showToast = useCustomToast();

    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});
    const [processData, setProcessData] = useState(undefined);
    const revisionDialogRef = useRef(null);

    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const bgTextarea = useColorModeValue("white", "navy.900");

    const ids = useMemo(() => ({
        decisaoRelator: "decisaoRelator",
        decisaoFinal: "decisaoFinal",
        votacao: "votacao",
        observacoesRevisao: "observacoesRevisao",
        revisao: "revisao",
    }), []);

    const processStatusOptions = [
        { value: PROCESS_STATUS.ACCEPTED, label: t("process.judgment.process-types.defer") },
        { value: PROCESS_STATUS.REJECTED, label: t("process.judgment.process-types.reject") },
    ];

    const poolDecisionOptions = [
        { value: POOL_DECISION.UNANIMOUS_ACCEPTANCE, label: t("process.fill-final-decision.form.pool-options.unanimous-acceptance") },
        { value: POOL_DECISION.ACCEPT_NOT_UNANIMOUSLY, label: t("process.fill-final-decision.form.pool-options.accept-not-unanimously") },
        { value: POOL_DECISION.DOES_NOT_COMPLY, label: t("process.fill-final-decision.form.pool-options.does-not-comply") },
    ];

    const isFormInvalid = () =>
        formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (process) => ({
        [ids.decisaoRelator]: {
            value: process?.decisao_relator || "",
            validations: [{ required: true }],
        },

        ...(process?.process_judgement_meetings_needed
            ? {
                [ids.decisaoFinal]: {
                    value: process?.decisao_final || "",
                    validations: [{ required: true }],
                },
                [ids.votacao]: {
                    value: process?.votacao || "",
                    validations: [{ required: true }],
                },
            }
        : {}),

        [ids.observacoesRevisao]: {
            value: process?.observacoes_revisao || "",
            validations: [],
        },
        [ids.revisao]: {
            value: process?.revisao || false,
            validations: [],
        },
    });

    useEffect(() => {
        if (id) {
            fetchProcess();
        }
    }, [id]);

    const fetchProcess = async () => {
        try {
            const process = await processesService.find(id);
            setProcessData(process);
            setFormData(initFormData(process));
        } catch (error) {
            showToast({ title: t("process.fill-final-decision.messages.errors.final-decision"), status: "error" });
            redirectToList();
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    function toDto() {
        return {
            ...processData,
            ...(judgementMeetingNeeded
                ? {
                    decisao_final: formData[ids.decisaoFinal].value,
                    votacao: formData[ids.votacao].value,
                }
                : {}),
            revisao: formData[ids.revisao].value,
            observacoes_revisao: formData[ids.observacoesRevisao].value,
        };
    }

    const handleSubmit = async () => {
        const payload = toDto();

        try {
            await processesService.finalDecision(id, payload);
            showToast({
                title: t("process.fill-final-decision.messages.success.send"),
                status: "success",
            });
            navigate(ROUTES.transportManagementPath + ROUTES.processes);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const handleRequestRevision = () => {
        if (formData[ids.observacoesRevisao].value) {
            handleConfirmRequestRevision();
        } else {
            revisionDialogRef.current?.displayDialog({
                title: t("process.fill-final-decision.confirm.title"),
                message: t("process.fill-final-decision.confirm.message"),
            });
        }
    };

    function toDtoRevision() {
        return {
            ...processData,
            revisao: true,
            observacoes_revisao: formData[ids.observacoesRevisao]?.value,
        };
    }

    const handleConfirmRequestRevision = async () => {
        const payload = toDtoRevision();

        try {
            await processesService.finalDecision(id, payload);
            showToast({
                title: t("process.fill-final-decision.messages.success.revision"),
                status: "success",
            });
            navigate(ROUTES.transportManagementPath + ROUTES.processes);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    const judgementMeetingNeeded = processData?.process_judgement_meetings_needed;

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt="80px">
                <ListPageCardHeader
                    title={t("process.fill-final-decision.form.title")}
                    subtitle={processData?.numero}
                />
                <CardBody px="22px">
                    {!!formData ? (
                        <Stack w="100%" direction="column" spacing="20px">
                            <FormControl isReadOnly isRequired>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t("process.fill-final-decision.form.relator-decision")}
                                </FormLabel>
                                <Select isDisabled={true} id={ids.decisaoRelator} value={formData[ids.decisaoRelator].value} isReadOnly>
                                    <option value={null}></option>
                                    {processStatusOptions.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            {judgementMeetingNeeded ? (
                                <>
                                    <FormControl isRequired isInvalid={inputError[ids.decisaoFinal]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t("process.fill-final-decision.form.final-decision")}
                                        </FormLabel>
                                        <Select id={ids.decisaoFinal} value={formData[ids.decisaoFinal].value} onChange={handleInputChange}>
                                            <option value={null}></option>
                                            {processStatusOptions.map(({ value, label }) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.decisaoFinal])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    <FormControl isRequired isInvalid={inputError[ids.votacao]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t("process.fill-final-decision.form.pool-decision")}
                                        </FormLabel>
                                        <Select id={ids.votacao} value={formData[ids.votacao].value} onChange={handleInputChange}>
                                            <option value={null}></option>
                                            {poolDecisionOptions.map(({ value, label }) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.votacao])}
                                        </FormErrorMessage>
                                    </FormControl>
                                </>
                            ) : (
                                <FormControl>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("process.fill-final-decision.form.revision-observation")}
                                    </FormLabel>
                                    <Textarea
                                        id={ids.observacoesRevisao}
                                        minH="120px"
                                        value={formData[ids.observacoesRevisao].value}
                                        onChange={handleInputChange}
                                        fontSize="xs"
                                        bg={bgTextarea}
                                        _placeholder={{ color: placeholderColor }}
                                    />
                                </FormControl>
                            )}

                            <Stack direction="row" justify="space-between">
                                <Button w="125px" bg="gray.100" onClick={() => navigate(ROUTES.transportManagementPath + ROUTES.processes)}>
                                    {t("system.actions.cancel")}
                                </Button>
                                <Stack direction="row" justify="end">
                                    {
                                        !judgementMeetingNeeded &&
                                        <Button
                                            w="160px"
                                            variant="outline"
                                            colorScheme="yellow"
                                            onClick={handleRequestRevision}>
                                            {t("process.fill-final-decision.form.request-revision")}
                                        </Button>
                                    }
                                    <Button w="125px" colorScheme="blue" isDisabled={isFormInvalid()} onClick={handleSubmit}>
                                        {t("system.actions.save")}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
            <AffirmativeDialog
                ref={revisionDialogRef}
                confirmButtonProps={{ colorScheme: "red" }}
            />
        </Flex>
    );
}
